import { useState } from 'react';
import { Upload, Link2 } from 'lucide-react';
import { adminUploadFile } from '@/lib/api';
import { inp } from './styles';

/* ─── File / URL toggle for Hero (file uploads optional) ─── */
export function FileOrUrlInput({ label, hint, accept, value, onChange, uploadType, testId }) {
  const initialMode = value && !value.startsWith('/api/uploads') ? 'url' : value ? 'file' : 'url';
  const [mode, setMode] = useState(initialMode);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState(value?.startsWith('/api/uploads') ? value.split('/').pop() : '');

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await adminUploadFile(file);
      onChange(result.url);
      setUploadedName(file.name);
    } catch (ex) {
      alert(ex.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-slate-400 text-xs font-medium">{label}</label>
        <div className="flex gap-1">
          <button type="button" onClick={() => setMode('file')} data-testid={`${testId}-mode-file`}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${mode === 'file' ? 'bg-[#F26522] text-white' : 'bg-white/5 text-slate-400 hover:text-white'}`}>
            <Upload size={12} /> File
          </button>
          <button type="button" onClick={() => setMode('url')} data-testid={`${testId}-mode-url`}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${mode === 'url' ? 'bg-[#F26522] text-white' : 'bg-white/5 text-slate-400 hover:text-white'}`}>
            <Link2 size={12} /> URL
          </button>
        </div>
      </div>

      {mode === 'file' ? (
        <div>
          <label className={`${inp} cursor-pointer flex items-center gap-3 hover:border-[#F26522]/30 transition-colors`}>
            <Upload size={16} className="text-[#F26522] shrink-0" />
            <span className="text-slate-400 text-sm truncate">
              {uploading ? 'Uploading...' : uploadedName || `Choose ${uploadType} file...`}
            </span>
            <input type="file" accept={accept} onChange={handleFileChange} className="hidden" data-testid={`${testId}-file`} disabled={uploading} />
          </label>
          {value && <p className="text-slate-500 text-xs mt-1 truncate">Current: {value}</p>}
        </div>
      ) : (
        <input
          type="url"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={`https://example.com/${uploadType === 'video' ? 'hero-video.mp4' : 'hero-poster.jpg'}`}
          data-testid={`${testId}-url`}
          className={inp}
        />
      )}
      <p className="text-slate-500 text-xs mt-1">{hint}</p>
    </div>
  );
}

/* ─── Media Input (File + URL toggle) for Gallery/Videos forms ─── */
export function MediaInput({ label, accept, fileType, value, onChange, onFileUpload, uploading, testId }) {
  const [mode, setMode] = useState('file');
  const [fileName, setFileName] = useState('');

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    onFileUpload(file);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-slate-400 text-xs font-medium">{label}</label>
        <div className="flex gap-1">
          <button type="button" onClick={() => setMode('file')} data-testid={`${testId}-mode-file`}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${mode === 'file' ? 'bg-[#F26522] text-white' : 'bg-white/5 text-slate-400 hover:text-white'}`}>
            <Upload size={12} /> File
          </button>
          <button type="button" onClick={() => setMode('url')} data-testid={`${testId}-mode-url`}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${mode === 'url' ? 'bg-[#F26522] text-white' : 'bg-white/5 text-slate-400 hover:text-white'}`}>
            <Link2 size={12} /> URL
          </button>
        </div>
      </div>

      {mode === 'file' ? (
        <label className={`${inp} cursor-pointer flex items-center gap-3 hover:border-[#F26522]/30 transition-colors`}>
          <Upload size={16} className="text-[#F26522] shrink-0" />
          <span className="text-slate-400 text-sm truncate">
            {uploading ? 'Uploading...' : fileName || `Choose ${fileType} file...`}
          </span>
          <input type="file" accept={accept} onChange={handleFile} className="hidden" data-testid={`${testId}-file`} disabled={uploading} />
        </label>
      ) : (
        <input
          type="url"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={`https://example.com/${fileType === 'video' ? 'video.mp4' : 'image.jpg'}`}
          data-testid={`${testId}-input`}
          required
          className={inp}
        />
      )}
      {value && <p className="text-slate-500 text-xs mt-1 truncate">Current: {value}</p>}
    </div>
  );
}
